import { trackEvent } from '@/lib/analytics'
import apiClient from '@/lib/api-client'
import type {
  ExternalJobListing,
  ExternalJobListingList,
  ExternalJobMatchRequest,
  ExternalJobMatchResponse,
  ExternalJobSearchParams,
  JobApplyResumeStatus,
  JobCategoriesResponse,
  JobCompanyTab,
  RecommendedJobsResponse,
} from '@/types'
import { isAxiosError } from 'axios'

export const externalJobsService = {
  async searchJobs(
    params: ExternalJobSearchParams = {},
    signal?: AbortSignal,
  ): Promise<ExternalJobListingList> {
    const response = await apiClient.get<ExternalJobListingList>('/api/v1/external-jobs', {
      ...(signal ? { signal } : {}),
      params: {
        query: params.query,
        location: params.location,
        country: params.country,
        is_remote: params.is_remote,
        job_type: params.job_type,
        salary_min: params.salary_min,
        category: params.category,
        skip: params.skip ?? 0,
        limit: params.limit ?? 20,
      },
    })
    return response.data
  },

  async getJob(jobId: number): Promise<ExternalJobListing> {
    const response = await apiClient.get<ExternalJobListing>(`/api/v1/external-jobs/${jobId}`)
    return response.data
  },

  /** `check: true` is poll mode -- reports resolution status without
   * kicking off a new server-side resolution. The first Company-tab fetch
   * omits it so a not-yet-resolved employer gets one resolution attempt;
   * the auto-poll and manual refresh always send it. */
  async getJobCompanyTab(jobId: number, opts?: { check?: boolean }): Promise<JobCompanyTab> {
    const url = `/api/v1/external-jobs/${jobId}/connections`
    const response = opts?.check
      ? await apiClient.get<JobCompanyTab>(url, { params: { check: true } })
      : await apiClient.get<JobCompanyTab>(url)
    return response.data
  },

  async getRecommendedJobs(limit = 10): Promise<RecommendedJobsResponse> {
    const response = await apiClient.get<RecommendedJobsResponse>(
      '/api/v1/external-jobs/recommended',
      { params: { limit } },
    )
    return response.data
  },

  async getCategories(): Promise<JobCategoriesResponse> {
    const response = await apiClient.get<JobCategoriesResponse>('/api/v1/external-jobs/categories')
    return response.data
  },

  async analyzeMatch(
    jobId: number,
    request?: ExternalJobMatchRequest,
    chatId?: string | null,
  ): Promise<ExternalJobMatchResponse> {
    const params = chatId ? { chat_id: chatId } : undefined
    const response = await apiClient.post<ExternalJobMatchResponse>(
      `/api/v1/external-jobs/${jobId}/match`,
      request ?? {},
      { params },
    )
    return response.data
  },

  async getMyMatches(skip = 0, limit = 20): Promise<RecommendedJobsResponse> {
    const response = await apiClient.get<RecommendedJobsResponse>(
      '/api/v1/external-jobs/matches/me',
      { params: { skip, limit } },
    )
    return response.data
  },

  async triggerMatchResume(
    documentId: number,
    chatId?: number | null,
    isMobile?: boolean,
  ): Promise<{ status: string; document_id: number }> {
    const params: { chat_id?: number; is_mobile?: boolean } = {}
    if (chatId) params.chat_id = chatId
    if (isMobile !== undefined) params.is_mobile = isMobile

    const response = await apiClient.post<{ status: string; document_id: number }>(
      `/api/v1/external-jobs/match-resume/${documentId}`,
      undefined,
      { params: Object.keys(params).length > 0 ? params : undefined },
    )
    trackEvent('match_set_created', {
      source_type: 'resume_scan',
      is_mobile: Boolean(isMobile),
    })
    return response.data
  },

  async startApplyResume(jobId: number): Promise<JobApplyResumeStatus> {
    const response = await apiClient.post<JobApplyResumeStatus>(
      `/api/v1/external-jobs/${jobId}/apply-resume`,
      { source: 'opportunities_apply_now' },
    )
    return response.data
  },

  /**
   * Batch counterpart to getApplyResumeStatus. Returns one entry per requested
   * job that has a run (jobs without a run are omitted). Lets the Opportunities
   * page restore every visible card in one request instead of one GET per card.
   */
  async getApplyResumeStatuses(jobIds: number[]): Promise<JobApplyResumeStatus[]> {
    const response = await apiClient.post<JobApplyResumeStatus[]>(
      '/api/v1/external-jobs/apply-resume/statuses',
      { job_ids: jobIds },
    )
    return response.data
  },

  /** Returns null when the user has no apply-resume run for this job (404). */
  async getApplyResumeStatus(jobId: number): Promise<JobApplyResumeStatus | null> {
    try {
      const response = await apiClient.get<JobApplyResumeStatus>(
        `/api/v1/external-jobs/${jobId}/apply-resume`,
      )
      return response.data
    } catch (err: unknown) {
      if (isAxiosError(err) && err.response?.status === 404) {
        return null
      }
      throw err
    }
  },

  async retryApplyResume(jobId: number): Promise<JobApplyResumeStatus> {
    const response = await apiClient.post<JobApplyResumeStatus>(
      `/api/v1/external-jobs/${jobId}/apply-resume/retry`,
    )
    return response.data
  },
}
